// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: edits one mail signature (Settings → Mail → Signatures): name, rich body with
// pictures, a preview of how it sits under a message, Save and Delete.

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {isBlankHtml, MailRichTextEditor} from '@app/features/konvo_mail/components/MailRichTextEditor';
import styles from '@app/features/konvo_mail/components/MailSignatureEditor.module.css';
import MailSignatures, {type MailSignature, type MailSignatureDoc} from '@app/features/konvo_mail/state/MailSignatures';
import {sanitizeSignatureHtml} from '@app/features/konvo_mail/utils/MailSignatureImport';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Input} from '@app/features/ui/components/form/FormInput';
import {OutlookLogo} from '@app/features/ui/components/icons/OutlookLogo';
import {ArrowLeftIcon} from '@app/features/ui/icons/Icons';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useId, useMemo, useState} from 'react';

interface MailSignatureEditorProps {
	/** null: a new signature. */
	signature: MailSignature | null;
	onClose: () => void;
}

export const MailSignatureEditor = observer(function MailSignatureEditor({
	signature,
	onClose,
}: MailSignatureEditorProps) {
	const {t} = useLingui();
	const nameId = useId();
	const bodyLabelId = useId();
	const [name, setName] = useState(signature?.name ?? '');
	const [html, setHtml] = useState(signature?.html ?? '');
	const [saving, setSaving] = useState(false);
	const [failed, setFailed] = useState<null | 'too_large' | 'failed'>(null);
	const preview = useMemo(() => sanitizeSignatureHtml(html), [html]);

	const trimmedName = name.trim();
	const blank = isBlankHtml(html);
	const changed = !signature || trimmedName !== signature.name || html !== signature.html;
	const canSave = trimmedName.length > 0 && !blank && changed && !saving;

	const handleSave = async () => {
		if (!canSave) return;
		setSaving(true);
		setFailed(null);
		const now = new Date().toISOString();
		const current = MailSignatures.doc;
		let doc: MailSignatureDoc;
		if (signature) {
			doc = {
				...current,
				signatures: current.signatures.map((s) =>
					s.id === signature.id ? {...s, name: trimmedName.slice(0, 60), html, updated_at: now} : s,
				),
			};
		} else {
			const id = MailSignatures.newId(trimmedName);
			const created: MailSignature = {id, name: trimmedName.slice(0, 60), html, source: 'konvo', updated_at: now};
			doc = {
				signatures: [...current.signatures, created],
				// The first signature someone makes is the one they mean to use.
				default_new: current.signatures.length === 0 ? id : current.default_new,
				default_reply: current.signatures.length === 0 ? id : current.default_reply,
			};
		}
		const ok = await MailSignatures.save(doc);
		setSaving(false);
		if (ok) onClose();
		else setFailed(MailSignatures.saveError ?? 'failed');
	};

	const handleDelete = () => {
		if (!signature) return;
		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={t`Delete signature`}
					description={t`"${signature.name}" will no longer be added to your mail.`}
					primaryText={t`Delete`}
					primaryVariant="danger"
					onPrimary={async () => {
						const ok = await MailSignatures.remove(signature.id);
						if (ok) onClose();
						else setFailed('failed');
					}}
					data-flx="konvo-mail.signature-editor.delete-confirm"
				/>
			)),
		);
	};

	return (
		<div className={styles.editor} data-flx="konvo-mail.signature-editor">
			<div className={styles.header}>
				<Tooltip text={t`Back to signatures`} position="top">
					<button
						type="button"
						className={styles.iconButton}
						onClick={onClose}
						aria-label={t`Back to signatures`}
						data-flx="konvo-mail.signature-editor.back"
					>
						<ArrowLeftIcon size={18} />
					</button>
				</Tooltip>
				<span className={styles.title}>{signature ? <Trans>Edit signature</Trans> : <Trans>New signature</Trans>}</span>
				{signature?.source === 'outlook' ? (
					<span className={styles.badge} data-flx="konvo-mail.signature-editor.outlook-badge">
						<OutlookLogo size={13} />
						<Trans>Imported from Outlook</Trans>
					</span>
				) : null}
			</div>

			<label className={styles.label} htmlFor={nameId}>
				<Trans>Name</Trans>
			</label>
			<div className={styles.nameField}>
				<Input
					id={nameId}
					value={name}
					maxLength={60}
					onChange={(event) => setName(event.target.value)}
					placeholder={t`For example: Full, or Short reply`}
					data-flx="konvo-mail.signature-editor.name"
				/>
			</div>

			<span className={styles.label} id={bodyLabelId}>
				<Trans>Signature</Trans>
			</span>
			<div
				className={styles.body}
				role="group"
				aria-labelledby={bodyLabelId}
				data-flx="konvo-mail.signature-editor.body"
			>
				<MailRichTextEditor
					value={html}
					onChange={setHtml}
					placeholder={t`Your name, role and how to reach you`}
					minHeight={150}
					allowImages
					toolbarClassName={styles.toolbar}
					surfaceClassName={styles.surface}
				/>
			</div>

			<span className={styles.label}>
				<Trans>How it looks in a message</Trans>
			</span>
			<div className={styles.preview} data-flx="konvo-mail.signature-editor.preview">
				<p className={styles.previewLine}>
					<Trans>Kind regards,</Trans>
				</p>
				{blank ? (
					<p className={styles.previewEmpty}>
						<Trans>Your signature shows here.</Trans>
					</p>
				) : (
					// Our own sanitised HTML (scripts, handlers and javascript: links stripped).
					<div className={styles.previewBody} dangerouslySetInnerHTML={{__html: preview}} />
				)}
			</div>

			{failed ? (
				<p className={styles.error} role="alert" data-flx="konvo-mail.signature-editor.error">
					{failed === 'too_large' ? (
						<Trans>That signature is too big to save. Try smaller pictures.</Trans>
					) : (
						<Trans>Couldn't save. Check your connection and try again.</Trans>
					)}
				</p>
			) : null}

			<div className={styles.footer}>
				{signature ? (
					<Button variant="secondary" small onClick={handleDelete} data-flx="konvo-mail.signature-editor.delete">
						<Trans>Delete</Trans>
					</Button>
				) : null}
				<span className={styles.spacer} />
				<Button variant="secondary" small onClick={onClose} data-flx="konvo-mail.signature-editor.cancel">
					<Trans>Cancel</Trans>
				</Button>
				<Button
					small
					onClick={handleSave}
					disabled={!canSave}
					submitting={saving}
					data-flx="konvo-mail.signature-editor.save"
				>
					<Trans>Save</Trans>
				</Button>
			</div>
		</div>
	);
});
